import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import type { Folder } from '../db/Types'
import {
	createFolder as apiCreateFolder,
	deleteFolder as apiDeleteFolder,
	fetchFolders,
	folderKeys,
	updateFolder as apiUpdateFolder,
} from '../api/queries/FolderQueries'
import { useOptimisticDelete, useOptimisticUpdate } from './UseOptimisticMutation'

interface UseFoldersDataReturn {
	folders: Folder[]
	isLoading: boolean
	error: string | null
	createFolder: (name: string) => Promise<Folder>
	updateFolder: (id: string, name: string) => Promise<void>
	deleteFolder: (id: string) => Promise<void>
	refetch: () => Promise<void>
}

export function useFoldersData(): UseFoldersDataReturn {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const handleUnauthorized = useCallback((error: Error) => {
		if (error.message === 'UNAUTHORIZED') {
			navigate('/login')
		}
	}, [navigate])

	// QUERY
	const {
		data: folders = [],
		isLoading,
		error: queryError,
	} = useQuery({
		queryKey: folderKeys.list(),
		queryFn: fetchFolders,
	})

	// Handle unauthorized errors
	useEffect(() => {
		if (queryError) {
			handleUnauthorized(queryError as Error)
		}
	}, [queryError, handleUnauthorized])

	// CREATE MUTATION
	const createMutation = useMutation({
		mutationFn: (data: { name: string }) =>
			apiCreateFolder(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: folderKeys.list() })
		},
		onError: handleUnauthorized,
	})

	// UPDATE MUTATION with optimistic update
	const updateFolder = useOptimisticUpdate<Folder, string, { name: string }>({
		mutationFn: async ({ id, data }) => {
			await apiUpdateFolder(id, data)
		},
		queryKey: folderKeys.list(),
		getId: (folder) => folder.id,
		applyUpdate: (folder, data) => ({ ...folder, name: data.name }),
		onError: handleUnauthorized,
	})

	// DELETE MUTATION with optimistic update
	const deleteFolder = useOptimisticDelete<Folder, string>({
		mutationFn: apiDeleteFolder,
		queryKey: folderKeys.list(),
		getId: (folder) => folder.id,
		onError: handleUnauthorized,
	})

	// WRAPPER FUNCTIONS (maintain API compatibility)
	const createFolder = async (name: string): Promise<Folder> => {
		return createMutation.mutateAsync({ name })
	}

	const updateFolderWrapper = async (id: string, name: string): Promise<void> => {
		await updateFolder(id, { name })
	}

	const deleteFolderWrapper = async (id: string): Promise<void> => {
		await deleteFolder(id)
	}

	const refetch = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: folderKeys.list() })
	}, [queryClient])

	return {
		folders,
		isLoading,
		error: queryError?.message || null,
		createFolder,
		updateFolder: updateFolderWrapper,
		deleteFolder: deleteFolderWrapper,
		refetch,
	}
}
